import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from './Components';
import { Button, Input, Select, Textarea } from './UI';
import { employeeService } from '../../services';
import { toast } from 'react-toastify';

const UserProfileModal = ({ isOpen, onClose, userId }) => {
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [sameAsPresentAddress, setSameAsPresentAddress] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeeService.getCurrentUserProfile(userId);
      if (response.data.success) {
        const data = response.data.data;
        console.log('Fetched user profile data:', data); // Debug log
        setUserData(data);
        setFormData(data);
        setOriginalData(data);
        // Check if addresses are the same based on sameAddress field or fallback to address comparison
        const isSame = data.sameAddress !== undefined ? data.sameAddress === 1 : (data.address === data.address2 && data.address && data.address2);
        setSameAsPresentAddress(isSame);
        // Set checkbox value in formData
        setFormData(prev => ({ ...prev, sameAddress: isSame ? 1 : 0 }));
        setOriginalData(prev => ({ ...prev, sameAddress: isSame ? 1 : 0 }));
      } else {
        console.error('Failed to fetch user profile:', response.data.message);
        toast.error('Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserProfile();
    }
  }, [isOpen, userId, fetchUserProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };

      // If present address is changed and checkbox is checked, update permanent address too
      if (field === 'address' && sameAsPresentAddress) {
        updatedData.address2 = value;
      }

      return updatedData;
    });
  };

  const handleSameAddressChange = (checked) => {
    setSameAsPresentAddress(checked);
    if (checked) {
      // Copy present address to permanent address
      setFormData(prev => ({
        ...prev,
        address2: prev.address || '',
        sameAddress: 1
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        sameAddress: 0
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await employeeService.updateCurrentUserProfile(userId, formData);
      if (response.data.success) {
        toast.success('Profile updated successfully');
        setOriginalData(formData);
        setUserData(formData);
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  const handleCancel = () => {
    setFormData(originalData);
    onClose();
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Edit User Profile" size="2xl" centerTitle={true} showCloseButton={false}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {loading ? (
          <div className="flex justify-center items-center py-5">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : userData ? (
          <div className="grid grid-cols-1 gap-8">
            {/* Basic Information */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Basic Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Input
                  label="Username"
                  value={formData.username || ''}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  required
                />

                <Input
                  label="Designation"
                  value={formData.designation || ''}
                  disabled
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Contact Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <Input
                    label="Email"
                    type="email"
                    value={formData.useremail || ''}
                    onChange={(e) => handleInputChange('useremail', e.target.value)}
                    disabled
                  />

                  <Input
                    label="Phone"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled
                  />
                </div>

                <div className="space-y-3">
                  <Input
                    label="Alternate Email"
                    type="email"
                    value={formData.alternatemail || ''}
                    onChange={(e) => handleInputChange('alternatemail', e.target.value)}
                  />

                  <Input
                    label="Alternate Phone"
                    value={formData.alternatphone || ''}
                    onChange={(e) => handleInputChange('alternatphone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Personal Details</h4>

              {/* Present Address and Permanent Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Textarea
                  label="Present Address"
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                />

                <Textarea
                  label="Permanent Address"
                  value={formData.address2 || ''}
                  onChange={(e) => handleInputChange('address2', e.target.value)}
                  rows={3}
                  disabled={sameAsPresentAddress}
                />
              </div>

              {/* Checkbox for same address */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sameAddress"
                  checked={sameAsPresentAddress}
                  onChange={(e) => handleSameAddressChange(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="sameAddress" className="text-sm text-green-600">
                  Permanent Address Same as Present Address
                </label>
              </div>

              {/* Aadhar Card, PAN Card, and Blood Group */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label="Aadhar Card No"
                  value={formData.aadhar || ''}
                  onChange={(e) => handleInputChange('aadhar', e.target.value)}
                  disabled
                />

                <Input
                  label="PAN Card No"
                  value={formData.pan || ''}
                  onChange={(e) => handleInputChange('pan', e.target.value)}
                  disabled
                />

                <Select
                  label="Blood Group"
                  value={formData.bloodgroup || ''}
                  onChange={(e) => handleInputChange('bloodgroup', e.target.value)}
                  disabled
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </Select>

                <Input
                  label="Date of Birth"
                  type="date"
                  value={formatDateForInput(formData.dob)}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  disabled
                />
              </div>
            </div>

            {/* Employment Information */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Employment Information 
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Joining</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
                    {formData.doj ? new Date(formData.doj).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </p>
                </div>

                <Select
                  label="Mediclaim"
                  value={formData.hasmediclame ? '1' : '0'}
                  onChange={(e) => handleInputChange('hasmediclame', e.target.value === '1' ? 1 : 0)}
                  disabled
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </Select>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejoin Status</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
                    {formData.isrejoin ? 'Rejoined' : 'Not Rejoined'}
                  </p>
                </div>
              </div>
            </div>

            {/* Passport Information */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Passport Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Passport Number"
                  value={formData.passport_no || ''}
                  onChange={(e) => handleInputChange('passport_no', e.target.value)}
                  placeholder={formData.passport_no ? formData.passport_no : "Not provided"}
                  disabled
                />

                <Input
                  label="Passport Valid Till"
                  type="date"
                  value={formatDateForInput(formData.passport_valid_till)}
                  onChange={(e) => handleInputChange('passport_valid_till', e.target.value)}
                  disabled
                />
              </div>
            </div>

            {/* Banking Information */}
            <div className="space-y-3">
              <h4 className="text-md font-semibold text-blue-500 border-b pb-2">Banking Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Account Number"
                  value={formData.account_number || ''}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  placeholder={formData.account_number ? formData.account_number : "Not provided"}
                  disabled
                />

                <Input
                  label="IFSC Code"
                  value={formData.ifsc_code || ''}
                  onChange={(e) => handleInputChange('ifsc_code', e.target.value)}
                  placeholder={formData.ifsc_code ? formData.ifsc_code : "Not provided"}
                  disabled
                  style={{ textTransform: 'uppercase' }}
                />

                <Input
                  label="Bank Name"
                  value={formData.bank_name || ''}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  placeholder={formData.bank_name ? formData.bank_name : "Not provided"}
                  disabled
                />
              </div>
              
              {/* Show banking status */}
              <div className="bg-gray-50 p-3 rounded-md border-l-4 border-green-400">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <p className="text-sm text-gray-700">
                        Disabled information is view-only. Contact <b>ADMIN</b> for changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-gray-500">No user data available</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-3 border-t">
          <Button
            type="button"
            onClick={handleCancel}
            variant="outline"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleReset}
            variant="secondary"
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            type="submit"
            disabled={saving || loading}
            loading={saving}
          >
            {saving ? 'Saving...' : 'Submit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserProfileModal;
